import {
    AssignmentInfo,
    ControlInfo,
    DateOption,
    EVAUATION_DATES,
    PREDEFINED_DATES,
    LessonInfo,
} from '@yandex-int/k-common/typings';
import { format, getDate, getDay, getMonth, getYear } from 'date-fns';
import { isNil } from 'lodash';

import { LESSON_DATES } from 'common.components/constants';
import { SourceAssignmentInfo } from 'common.components/lesson/typings';
import { getIsLessonEvaluable, getLessonDateTime } from 'utils/lesson';
import { LessonDate } from 'utils/lesson/lesson-time/types';

interface NoDeadlineToastParams {
    fromTime: string;
    fromDay: number;
    fromDate: string;
}

interface SameDayToastParams {
    fromTime: string;
    fromDay: number;
    fromDate: string;
    untilTime: string;
}

interface WithDeadlineToastParams {
    fromTime: string;
    fromDay: number;
    fromDate: string;
    untilTime: string;
    untilDay: string;
    untilDate: string;
}

type MessageValues = NoDeadlineToastParams | SameDayToastParams | WithDeadlineToastParams;

interface ToastParams {
    headerId: string;
    messageId: string;
    messageValues: MessageValues;
}

type ToastFunc = (dateAssign: Date, dateDeadline: Date | null) => ToastParams;

const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
        getYear(date1) === getYear(date2) && getMonth(date1) === getMonth(date2) && getDate(date1) === getDate(date2)
    );
};

export const getAssignLessonToast: ToastFunc = (dateAssign, dateDeadline) => {
    const formattedAssignDate = format(dateAssign, 'dd.MM');
    const formattedAssignTime = format(dateAssign, 'HH:mm');
    const assignDayOfWeek = getDay(dateAssign);

    if (isNil(dateDeadline)) {
        // тост без дедлана
        return {
            headerId: 'toast.assignedLesson.headerWithClass',
            messageId: 'toast.assignedLesson.withoutDeadline',
            messageValues: {
                fromTime: formattedAssignTime,
                fromDay: assignDayOfWeek,
                fromDate: formattedAssignDate,
            },
        };
    }

    if (isSameDay(dateAssign, dateDeadline)) {
        // выдача и дедлайн в один день
        const formattedDeadlineTime = format(dateDeadline, 'HH:mm');

        return {
            headerId: 'toast.assignedLesson.headerWithClass',
            messageId: 'toast.assignedLesson.withDeadlineSameDay',
            messageValues: {
                fromTime: formattedAssignTime,
                fromDay: assignDayOfWeek,
                fromDate: formattedAssignDate,
                untilTime: formattedDeadlineTime,
            },
        };
    }

    const formattedDeadlineDate = format(dateDeadline, 'dd.MM');
    const formattedDeadlineTime = format(dateDeadline, 'HH:mm');
    const deadlineDayOfWeek = getDay(dateDeadline);

    // выдача и дедлайн в разные дни
    return {
        headerId: 'toast.assignedLesson.headerWithClass',
        messageId: 'toast.assignedLesson.withDeadline',
        messageValues: {
            fromTime: formattedAssignTime,
            fromDay: assignDayOfWeek,
            fromDate: formattedAssignDate,
            untilTime: formattedDeadlineTime,
            untilDay: deadlineDayOfWeek,
            untilDate: formattedDeadlineDate,
        },
    };
};

const mapDateToPredefined = (dateType: LESSON_DATES): PREDEFINED_DATES => {
    switch (dateType) {
        case LESSON_DATES.AFTER_ASSIGN:
            return PREDEFINED_DATES.NOW;
        case LESSON_DATES.IN_FIFTEEN_MINUTES:
            return PREDEFINED_DATES.IN_FIFTEEN_MINUTES;
        default:
            throw Error(`not defined date preset ${dateType}`);
    }
};

// eslint-disable-next-line complexity
export function mapDates(date: LessonDate, now: Date): DateOption<Date | null> {
    switch (date.type) {
        case LESSON_DATES.CUSTOM:
            return { date: date.customDate };
        case LESSON_DATES.NONE:
            return { date: null };
        case LESSON_DATES.TODAY_MORNING:
        case LESSON_DATES.NEXT_WEEK_MONDAY:
        case LESSON_DATES.MONDAY_IN_TWO_WEEKS:
        case LESSON_DATES.TODAY_AFTERNOON:
        case LESSON_DATES.FRIDAY_AFTERNOON:
        case LESSON_DATES.TOMORROW_MORNING:
            return { date: getLessonDateTime(now, date.type) };
        default:
            return {
                predefined: mapDateToPredefined(date.type),
                timezone: -new Date().getTimezoneOffset(),
            };
    }
}

export const mapFrontAssignmentInfoToBack = (info: SourceAssignmentInfo, now: Date): AssignmentInfo => {
    const { lessonMode, assignmentDate, deadlineDate, duration, evaluationDate } = info;
    let genericDates: LessonInfo | ControlInfo | null = null;

    if (getIsLessonEvaluable(lessonMode)) {
        const dates: ControlInfo = {
            assignmentDate: mapDates(assignmentDate, now) as DateOption<Date>,
            startDeadline: mapDates(deadlineDate, now) as DateOption<Date>,
            duration: duration,
            evaluationDate: evaluationDate
                ? { date: evaluationDate }
                : {
                      predefined: EVAUATION_DATES.EQUAL_DEADLIINE,
                      timezone: -new Date().getTimezoneOffset(),
                  },
        };

        genericDates = dates;
    } else {
        const dates: LessonInfo = {
            assignmentDate: mapDates(assignmentDate, now) as DateOption<Date>,
            finishDeadline: mapDates(deadlineDate, now),
        };

        genericDates = dates;
    }

    return {
        lessonMode,
        ...genericDates,
    };
};
